import React, { useState, useRef } from 'react';
import { api, trackEvent } from './App';
import { Download, Upload, ArrowLeft, Check } from './Icons';

const API = process.env.REACT_APP_API_URL || '';

function apiUpload(path, formData) {
  const token = localStorage.getItem('sqetrack_token') || '';
  return fetch(API + path, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: formData,
  }).then(r => r.json());
}

function downloadTemplate() {
  const token = localStorage.getItem('sqetrack_token') || '';
  fetch(API + '/api/export/template', {
    headers: { 'Authorization': `Bearer ${token}` },
  }).then(r => r.blob()).then(blob => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'SQETrack_Template.xlsx'; a.click();
    URL.revokeObjectURL(url);
    trackEvent('template_download');
  });
}

// ── Template import ────────────────────────────────────────────────────────────

function TemplateImport({ onDone }) {
  const [phase, setPhase] = useState('upload'); // upload | preview | done
  const [updates, setUpdates] = useState([]);
  const [skipped, setSkipped] = useState(0);
  const [errors, setErrors] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileRef = useRef();

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true); setUploadError('');
    const fd = new FormData();
    fd.append('file', file);
    const res = await apiUpload('/api/import/spreadsheet?mode=template', fd);
    setUploading(false);
    if (res.error) { setUploadError(res.error); return; }
    setUpdates(res.updates || []);
    setSkipped(res.skipped || 0);
    setErrors(res.errors || []);
    setPhase('preview');
  };

  const apply = async () => {
    setApplying(true);
    await api('/api/import/apply', { method: 'POST', body: JSON.stringify({ updates }) });
    setApplying(false);
    trackEvent('spreadsheet_import', { source: 'template', topics_imported: updates.length });
    setPhase('done');
  };

  if (phase === 'done') {
    return (
      <div className="si-result">
        <div className="si-result-check">✓</div>
        <div className="si-result-title">Import complete</div>
        <div className="si-result-sub">{updates.length} topics updated.</div>
        <button className="ob-btn" style={{ marginTop: 20 }} onClick={onDone}>Continue</button>
      </div>
    );
  }

  if (phase === 'preview') {
    return (
      <div className="si-preview">
        <div className="si-preview-stats">
          <div className="si-stat"><span className="si-stat-n" style={{ color: 'var(--green)' }}>{updates.length}</span><span className="si-stat-l">topics matched</span></div>
          <div className="si-stat"><span className="si-stat-n">{skipped}</span><span className="si-stat-l">rows skipped</span></div>
          {errors.length > 0 && <div className="si-stat"><span className="si-stat-n" style={{ color: 'var(--amber)' }}>{errors.length}</span><span className="si-stat-l">unmatched</span></div>}
        </div>
        {errors.length > 0 && (
          <details className="si-errors">
            <summary style={{ fontSize: 12, color: 'var(--muted)', cursor: 'pointer' }}>View unmatched rows</summary>
            {errors.map((e, i) => <div key={i} className="si-error-row">{e}</div>)}
          </details>
        )}
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button className="ob-btn" onClick={apply} disabled={applying || updates.length === 0}>
            {applying ? 'Importing…' : `Import ${updates.length} topics`}
          </button>
          <button className="scraper-discard-btn" onClick={() => setPhase('upload')}>Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="si-upload">
      <p className="si-upload-hint">
        Download the template, fill in your confidence levels using the <strong>dropdown in column D</strong> (<em>Not covered, Weak, Moderate, Good, Strong</em>), then upload it back here.
        <br /><br />
        <strong>Already exported your data?</strong> You can upload a previous SQETrack export (.xlsx) directly — the format is detected automatically.
      </p>
      <button className="settings-action-btn" style={{ marginBottom: 16 }} onClick={downloadTemplate}>
        <Download size={13} /> Download template
      </button>
      <div className="si-dropzone" onClick={() => fileRef.current?.click()}>
        <input ref={fileRef} type="file" accept=".xlsx,.csv" style={{ display: 'none' }} onChange={handleFile} />
        {uploading ? 'Parsing…' : 'Click to upload your file (.xlsx or .csv)'}
      </div>
      {uploadError && <div className="scraper-error" style={{ marginTop: 8 }}>{uploadError}</div>}
    </div>
  );
}

// ── Custom import ──────────────────────────────────────────────────────────────

function CustomImport({ onDone }) {
  const [phase, setPhase] = useState('upload'); // upload | map | preview | done
  const [headers, setHeaders] = useState([]);
  const [sampleRows, setSampleRows] = useState([]);
  const [topicCol, setTopicCol] = useState('');
  const [scoreCol, setScoreCol] = useState('');
  const [statusCol, setStatusCol] = useState('');
  const [notesCol, setNotesCol] = useState('');
  const [file, setFile] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [unmatched, setUnmatched] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef();

  const handleDetect = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f); setUploading(true); setError('');
    const fd = new FormData();
    fd.append('file', f);
    const res = await apiUpload('/api/import/spreadsheet?mode=detect', fd);
    if (res.error) { setUploading(false); setError(res.error); return; }

    // If this looks like a SQETrack export (has Main Topic + Confidence % columns),
    // skip the column mapper and parse it directly via the template endpoint.
    const hdrs = res.headers || [];
    if (hdrs.includes('Main Topic') && hdrs.includes('Confidence %')) {
      const fd2 = new FormData();
      fd2.append('file', f);
      const res2 = await apiUpload('/api/import/spreadsheet?mode=template', fd2);
      setUploading(false);
      if (res2.error) { setError(res2.error); return; }
      setUpdates(res2.updates || []);
      setUnmatched(res2.errors ? res2.errors.map(e => ({ topic_name: e })) : []);
      setPhase('preview');
      return;
    }

    setUploading(false);
    setHeaders(hdrs);
    setSampleRows(res.sample_rows || []);
    setTopicCol(hdrs[0] || '');
    setScoreCol(hdrs[1] || '');
    setStatusCol('');
    setNotesCol('');
    setPhase('map');
  };

  const handleMatch = async () => {
    if (!file || !topicCol || !scoreCol) return;
    setUploading(true); setError('');
    const fd = new FormData();
    fd.append('file', file);
    fd.append('topic_col', topicCol);
    fd.append('score_col', scoreCol);
    if (statusCol) fd.append('status_col', statusCol);
    if (notesCol) fd.append('notes_col', notesCol);
    const res = await apiUpload('/api/import/spreadsheet?mode=match', fd);
    setUploading(false);
    if (res.error) { setError(res.error); return; }
    setUpdates(res.updates || []);
    setUnmatched(res.unmatched || []);
    setPhase('preview');
  };

  const apply = async () => {
    setApplying(true);
    await api('/api/import/apply', { method: 'POST', body: JSON.stringify({ updates }) });
    setApplying(false);
    trackEvent('spreadsheet_import', { source: 'custom', topics_imported: updates.length });
    setPhase('done');
  };

  if (phase === 'done') {
    return (
      <div className="si-result">
        <div className="si-result-check">✓</div>
        <div className="si-result-title">Import complete</div>
        <div className="si-result-sub">{updates.length} topics updated.</div>
        <button className="ob-btn" style={{ marginTop: 20 }} onClick={onDone}>Continue</button>
      </div>
    );
  }

  if (phase === 'preview') {
    return (
      <div className="si-preview">
        <div className="si-preview-stats">
          <div className="si-stat"><span className="si-stat-n" style={{ color: 'var(--green)' }}>{updates.length}</span><span className="si-stat-l">matched</span></div>
          <div className="si-stat"><span className="si-stat-n" style={{ color: 'var(--amber)' }}>{unmatched.length}</span><span className="si-stat-l">unmatched</span></div>
        </div>
        {updates.length > 0 && (
          <div className="si-match-list">
            {updates.slice(0, 8).map((u, i) => (
              <div key={i} className="si-match-row">
                <span className="si-match-orig">{u.topic_name}</span>
                <span className="si-match-arrow">→</span>
                <span className="si-match-to">{u.matched_to}</span>
                <span className={`si-match-tag ${u.module === 'FLK1' ? 'tag-flk1' : 'tag-flk2'}`}>{u.module}</span>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>{Math.round(u.confidence * 100)}%</span>
              </div>
            ))}
            {updates.length > 8 && <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>…and {updates.length - 8} more</div>}
          </div>
        )}
        {error && <div className="scraper-error" style={{ marginTop: 8 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button className="ob-btn" onClick={apply} disabled={applying || updates.length === 0}>
            {applying ? 'Importing…' : `Import ${updates.length} topics`}
          </button>
          <button className="scraper-discard-btn" onClick={() => setPhase('map')}>Back</button>
        </div>
      </div>
    );
  }

  if (phase === 'map') {
    return (
      <div className="si-map">
        <p className="si-upload-hint" style={{ marginBottom: 16 }}>
          Select which columns contain the topic names and scores.
        </p>
        {sampleRows.length > 0 && (
          <div className="si-sample">
            <div className="si-sample-row si-sample-head">
              {headers.map((h, i) => <span key={i}>{h}</span>)}
            </div>
            {sampleRows.slice(0, 3).map((row, i) => (
              <div key={i} className="si-sample-row">
                {row.map((cell, j) => <span key={j}>{cell}</span>)}
              </div>
            ))}
          </div>
        )}
        <div className="si-map-selects">
          <div>
            <label className="ob-label">Topic name column <span style={{ color: 'var(--red)' }}>*</span></label>
            <select className="settings-select" value={topicCol} onChange={e => setTopicCol(e.target.value)}>
              {headers.map(h => <option key={h} value={h}>{h}</option>)}
            </select>
          </div>
          <div>
            <label className="ob-label">Confidence / score column <span style={{ color: 'var(--red)' }}>*</span></label>
            <select className="settings-select" value={scoreCol} onChange={e => setScoreCol(e.target.value)}>
              {headers.map(h => <option key={h} value={h}>{h}</option>)}
            </select>
          </div>
          <div>
            <label className="ob-label">Status column <span style={{ color: 'var(--muted)', fontWeight: 400 }}>(optional)</span></label>
            <select className="settings-select" value={statusCol} onChange={e => setStatusCol(e.target.value)}>
              <option value="">— None —</option>
              {headers.map(h => <option key={h} value={h}>{h}</option>)}
            </select>
          </div>
          <div>
            <label className="ob-label">Notes column <span style={{ color: 'var(--muted)', fontWeight: 400 }}>(optional)</span></label>
            <select className="settings-select" value={notesCol} onChange={e => setNotesCol(e.target.value)}>
              <option value="">— None —</option>
              {headers.map(h => <option key={h} value={h}>{h}</option>)}
            </select>
          </div>
        </div>
        {error && <div className="scraper-error" style={{ marginTop: 8 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button className="ob-btn" onClick={handleMatch} disabled={uploading}>
            {uploading ? 'Matching…' : 'Match topics →'}
          </button>
          <button className="scraper-discard-btn" onClick={() => setPhase('upload')}>Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="si-upload">
      <p className="si-upload-hint">
        Upload any spreadsheet with topic names and confidence scores. SQETrack will match them to your 259 topics.
      </p>
      <div className="si-dropzone" onClick={() => fileRef.current?.click()}>
        <input ref={fileRef} type="file" accept=".xlsx,.csv" style={{ display: 'none' }} onChange={handleDetect} />
        {uploading ? 'Reading columns…' : 'Click to upload your spreadsheet (.xlsx or .csv)'}
      </div>
      {error && <div className="scraper-error" style={{ marginTop: 8 }}>{error}</div>}
    </div>
  );
}

// ── Main SpreadsheetImport component ──────────────────────────────────────────

export default function SpreadsheetImport({ onBack, onDone }) {
  const [card, setCard] = useState(null); // null | 'template' | 'custom'

  if (card === 'template') {
    return (
      <div className="si-wrap">
        <button className="si-back-btn" onClick={() => setCard(null)}>
          <ArrowLeft size={13} /> Back
        </button>
        <div className="si-title">Use our template</div>
        <TemplateImport onDone={onDone} />
      </div>
    );
  }

  if (card === 'custom') {
    return (
      <div className="si-wrap">
        <button className="si-back-btn" onClick={() => setCard(null)}>
          <ArrowLeft size={13} /> Back
        </button>
        <div className="si-title">Import your spreadsheet</div>
        <CustomImport onDone={onDone} />
      </div>
    );
  }

  return (
    <div className="si-wrap">
      {onBack && (
        <button className="si-back-btn" onClick={onBack}>
          <ArrowLeft size={13} /> Back
        </button>
      )}
      <div className="si-title">Import data</div>
      <div className="si-cards">
        <button className="si-card" onClick={() => setCard('template')}>
          <div className="si-card-icon"><Download size={22} /></div>
          <div className="si-card-title">Use our template</div>
          <div className="si-card-text">Download a pre-filled spreadsheet with all 259 topics. Fill in your confidence scores, then upload it back. Also accepts a previous SQETrack export.</div>
          <div className="si-card-cta">Download template →</div>
        </button>
        <button className="si-card" onClick={() => setCard('custom')}>
          <div className="si-card-icon"><Upload size={22} /></div>
          <div className="si-card-title">Import your spreadsheet</div>
          <div className="si-card-text">Already tracking topics in your own spreadsheet? Upload it and we'll match your scores to SQETrack topics.</div>
          <div className="si-card-cta">Upload spreadsheet →</div>
        </button>
      </div>
    </div>
  );
}
